import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * 2Col Component
 * A simple two-column layout with text content on one side and image on the other
 * Default: Text on left, Image on right
 */
const TwoCol = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    image = null,
    layout = "text-left", // "text-left" or "text-right"
    component_name = "2Col",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localImage, setLocalImage] = useState(image);
  const [localLayout, setLocalLayout] = useState(layout);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
    setLocalImage(image);
    setLocalLayout(layout || "text-left");
  }, [id, title, description, image, layout]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      image: localImage,
      layout: localLayout,
    });
    setIsEditing(false);
  };

  const handleImageChange = (newImage) => {
    setLocalImage(newImage);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component two-col-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Two Column Layout</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Layout</label>
          <select
            className="form-select"
            value={localLayout}
            onChange={(e) => setLocalLayout(e.target.value)}
          >
            <option value="text-left">Text Left, Image Right</option>
            <option value="text-right">Text Right, Image Left</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title/Heading</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter heading"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description (supports multiple paragraphs)"
            rows={6}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Image</label>
          <EmailImageUpload
            initialImage={localImage}
            onImageUpload={handleImageChange}
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  // Split description by double newlines to support multiple paragraphs
  const paragraphs = localDescription
    ? localDescription.split(/\n\n+/).filter((p) => p.trim())
    : [];

  const imageUrl = getImageUrl(localImage);
  const isTextLeft = localLayout === "text-left";

  return (
    <>
      {!previewMode && (
        <div className="email-component two-col mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            style={{
              padding: "24px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              border="0"
              style={{
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <tr>
                {/* Text Column */}
                {isTextLeft ? (
                  <>
                    <td
                      width="50%"
                      valign="top"
                      style={{
                        padding: "16px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      {localTitle && (
                        <h2
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            marginBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {localTitle}
                        </h2>
                      )}

                      {paragraphs.map((paragraph, index) => (
                        <p
                          key={index}
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            marginBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {paragraph.trim()}
                        </p>
                      ))}
                    </td>

                    {/* Image Column */}
                    <td
                      width="50%"
                      valign="top"
                      align="center"
                      style={{
                        padding: "16px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      <table
                        width="100%"
                        cellPadding="0"
                        cellSpacing="0"
                        border="0"
                        style={{
                          border: "2px solid #ff6b6b",
                          backgroundColor: "#ffe5e5",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{
                              padding: "20px",
                              minHeight: "300px",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={localTitle || "Image"}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <img
                                src="/img-preview.svg"
                                alt="Image placeholder"
                                width="80"
                                height="80"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  display: "block",
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      </table>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Image Column (Left) */}
                    <td
                      width="50%"
                      valign="top"
                      align="center"
                      style={{
                        padding: "16px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      <table
                        width="100%"
                        cellPadding="0"
                        cellSpacing="0"
                        border="0"
                        style={{
                          border: "2px solid #ff6b6b",
                          backgroundColor: "#ffe5e5",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{
                              padding: "20px",
                              minHeight: "300px",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={localTitle || "Image"}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <img
                                src="/img-preview.svg"
                                alt="Image placeholder"
                                width="80"
                                height="80"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  display: "block",
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      </table>
                    </td>

                    {/* Text Column (Right) */}
                    <td
                      width="50%"
                      valign="top"
                      style={{
                        padding: "16px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      {localTitle && (
                        <h2
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            marginBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {localTitle}
                        </h2>
                      )}

                      {paragraphs.map((paragraph, index) => (
                        <p
                          key={index}
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            marginBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {paragraph.trim()}
                        </p>
                      ))}
                    </td>
                  </>
                )}
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </>
  );
};

export default TwoCol;

